import { useState } from 'react';

import { Button } from '@components/button';
import { CodeBlock } from '@components/code-block';
import { useLocalStorageState } from '@components/component-helpers';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { RelativeSpinner } from '@components/loading-spinner';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Stepper } from '@components/stepper';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { FieldTable, getFormEndpointVariables } from '../common';
import { getExampleValueFiles } from '../endpoint-card';
import { endpointContext } from '../endpoint-fields';
import {
  ListsScopeProvider,
  useIntegrationEndpoint,
  useIntegrationTestRequest,
  useUpdateIntegrationEndpointExampleFiles,
} from '../integrations-hooks';
import type {
  IntegrationEndpoint,
  IntegrationEndpointVariable,
  IntegrationTestRequestType,
} from '../integrations-types';
import { RequestDetailsRequestBody, RequestDetailsUrlRow } from '../request-details';

export const useTestRequestModal = () => useModal(TestRequest, { variant: 'LARGE' });

const REQUEST_DETAILS_OPEN_KEY = 'integration-test-request-details-open';

type StepType = 'values' | 'response';

type Props = {
  endpointId: string;
  templateId?: string;
};

const MODAL_FOOTER_ID = 'test-request-modal-footer';

const TestRequest = ({ endpointId, templateId }: Props) => {
  return (
    <ListsScopeProvider templateId={templateId}>
      <TestRequestContent endpointId={endpointId} templateId={templateId} />
    </ListsScopeProvider>
  );
};

const TestRequestContent = ({ endpointId, templateId }: Props) => {
  const { t } = useScopedTranslate(locale);
  const [step, setStep] = useState<StepType>('values');
  const [response, setResponse] = useState<IntegrationTestRequestType>();
  const endpointQuery = useIntegrationEndpoint(endpointId);
  const endpoint = endpointQuery.data;
  return (
    <Modal>
      <Modal.Header>
        <Modal.HeaderTitle title={t('testRequest')} />
      </Modal.Header>
      <Modal.Content>
        {!endpoint || endpointQuery.isLoading ? (
          <RelativeSpinner />
        ) : (
          <>
            {step === 'values' && (
              <VariableValues
                setStep={setStep}
                endpoint={endpoint}
                setResponse={setResponse}
                templateId={templateId}
              />
            )}
            {step === 'response' && response && (
              <TestRequestResponse response={response} endpointRequestType={endpoint.requestType} />
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Footer>
        <div id={MODAL_FOOTER_ID} />
        {step === 'response' && (
          <Stack space={4} orientation="horizontal" justify="flex-end">
            <Button
              iconLeft={<IconArrowLeft />}
              text={t('core.back')}
              variant="secondary"
              onClick={() => setStep('values')}
            />
            <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
          </Stack>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const TestStepper = ({ currentIndex }: { currentIndex: number }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stepper
      items={[{ label: t('setup') }, { label: t('viewResponse') }]}
      activeIndex={currentIndex}
      showCompleted
    />
  );
};
type Data = {};

const Form = createForm<Data>();

const VariableValues = ({
  endpoint,
  templateId,
  setStep,
  setResponse,
}: {
  endpoint: IntegrationEndpoint;
  templateId?: string;
  setStep: (value: StepType) => void;
  setResponse: (response: IntegrationTestRequestType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const endpointVariables = endpoint.variables as IntegrationEndpointVariable[];
  const testMutation = useIntegrationTestRequest(endpoint.shortId);
  const filesMutation = useUpdateIntegrationEndpointExampleFiles();
  const visibleColumns = {
    name: true,
    type: false,
    label: false,
    example: true,
    default: false,
    visible: false,
    required: false,
    format: false,
  };

  const handleUpdate = async () => {
    const variables = getFormEndpointVariables({
      formStore,
      endpointVariables,
      visibleColumns,
    });

    const data = new FormData();
    getExampleValueFiles(variables, variables, data);

    if (Array.from(data.keys()).length > 0) {
      await filesMutation.mutateAsync({
        shortId: endpoint.shortId,
        data,
      });
    }

    const response = await testMutation.mutateAsync({
      variables,
    });

    setResponse(response);
    setStep('response');
  };

  return (
    <Stack space={6}>
      <TestStepper currentIndex={0} />
      <Form store={formStore} onSubmit={handleUpdate}>
        <endpointContext.Provider value={endpoint}>
          <FieldTable
            form={Form}
            endpointId={endpoint.shortId}
            endpointVariables={endpointVariables}
            visibleColumns={visibleColumns}
            listTemplateId={templateId}
          />
        </endpointContext.Provider>
        <Form.Actions align="center" portal={MODAL_FOOTER_ID}>
          <Button variant="secondary" text={t('core.close')} onClick={hideModal} />
          <Form.Submit
            stopPropagation
            iconLeft={<IconSend />}
            isDisabled={testMutation.isLoading}
            text={t('test')}
            variant="primary-blue"
          />
        </Form.Actions>
      </Form>
    </Stack>
  );
};

const TestRequestResponse = ({
  response,
  endpointRequestType,
}: {
  response: IntegrationTestRequestType;
  endpointRequestType?: string | null;
}) => {
  const { t } = useScopedTranslate(locale);
  const { data, statusCode, success } = response;
  const [isRequestOpen, setRequestOpen] = useLocalStorageState({
    key: REQUEST_DETAILS_OPEN_KEY,
    defaultValue: true,
  });

  return (
    <Stack space={6}>
      <TestStepper currentIndex={1} />
      <Stack space={4}>
        <Stack orientation="horizontal" align="center">
          <IconStatusIndicator color={success ? 'elementGreen200' : 'elementRed100'} />
          <Text
            font="regular14"
            color={success ? 'elementGreen200' : 'elementRed100'}
            text={t(success ? 'success' : 'fail', { status: statusCode })}
          />
        </Stack>
        <CodeBlock header={t('response')} data={data} />
        <HideContainer
          title={t('requestDetails')}
          isOpen={isRequestOpen}
          onClick={() => setRequestOpen((open) => !open)}
        >
          <Stack space={3}>
            <RequestDetailsUrlRow url={response.url} />
            <RequestDetailsRequestBody
              bodyLabel={t('requestBody')}
              headersLabel={t('requestHeaders')}
              data={{
                endpointRequestType,
                requestHeaders: response.requestHeaders,
                requestData: response.requestData,
                requestFiles: response.requestFiles,
                graphqlQuery: response.graphqlQuery,
              }}
            />
          </Stack>
        </HideContainer>
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    setup: 'Setup',
    testRequest: 'Test request',
    updateValues: 'Update values',
    viewResponse: 'Response',
    requestData: 'Request data',
    test: 'Test',
    back: 'Back',
    success: 'Success • {status}',
    fail: 'Fail • {status}',
    response: 'RESPONSE',
    requestDetails: 'Request details',
    requestBody: 'REQUEST BODY',
    requestHeaders: 'REQUEST HEADERS',
  },
};
